/**
 * useAdminUsers Hooks
 * React Query hooks for user management with optimistic updates
 * 
 * Implements T056 and T057:
 * - useAdminUsers: List users with pagination and filters
 * - useCreateUser: Create user mutation
 * - useUpdateUser: Update user mutation
 * - useDeleteUser: Delete user mutation
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminUsersApi } from '../services/adminUsersApi';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminUser,
  CreateUserPayload,
  UpdateUserPayload,
  UserListParams,
  SendWelcomeEmailResponse,
} from '../types/admin-user.type';

// Query keys for cache management
export const userKeys = {
  all: ['admin', 'users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UserListParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated users list
 * 
 * @param params - Query parameters (page, pageSize, search, filters)
 * @returns Query result with paginated users
 * 
 * Features:
 * - 30s stale time to reduce API calls
 * - keepPreviousData for smooth pagination
 * - Total count reflects filtered results
 * 
 * @example
 * const { data, isLoading, error } = useAdminUsers({
 *   page: 1,
 *   pageSize: 50,
 *   search: 'john',
 *   role: 'CARE_SEEKER'
 * });
 */
export function useAdminUsers(
  params: UserListParams = {},
): UseQueryResult<PaginatedResponse<AdminUser>, Error> {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => adminUsersApi.listUsers(params),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data during refetch
  });
}

/**
 * Hook to fetch single user by ID
 * 
 * @param id - User ID
 * @returns Query result with user details
 */
export function useAdminUser(
  id: string,
): UseQueryResult<AdminUser, Error> {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => adminUsersApi.getUser(id),
    enabled: !!id,
  });
}

/**
 * Hook to create new user
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Invalidates users list on success
 * - Returns created user immediately
 * 
 * @example
 * const createUser = useCreateUser();
 * 
 * await createUser.mutateAsync({
 *   email: 'user@example.com',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   role: 'CARE_SEEKER'
 * });
 */
export function useCreateUser(): UseMutationResult<
  AdminUser,
  Error,
  CreateUserPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    // Wrapped so React Query's second mutationFn argument is not forwarded on.
    mutationFn: (payload: CreateUserPayload) => adminUsersApi.createUser(payload),
    onSuccess: () => {
      // Invalidate all user lists to refetch with new user
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Hook to update existing user
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic update (immediate UI update)
 * - Rollback on error
 * - Updates cache with new data
 * 
 * @example
 * const updateUser = useUpdateUser();
 * 
 * await updateUser.mutateAsync({
 *   id: 'user_123',
 *   payload: { firstName: 'Jane' }
 * });
 */
export function useUpdateUser(): UseMutationResult<
  AdminUser,
  Error,
  { id: string; payload: UpdateUserPayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => adminUsersApi.updateUser(id, payload),
    
    // Optimistic update
    onMutate: async ({ id, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: userKeys.detail(id) });

      // Snapshot previous value
      const previousUser = queryClient.getQueryData<AdminUser>(
        userKeys.detail(id),
      );

      // Optimistically update cache
      if (previousUser) {
        queryClient.setQueryData<AdminUser>(userKeys.detail(id), {
          ...previousUser,
          ...payload,
        });
      }

      return { previousUser };
    },
    
    // Rollback on error
    onError: (_error, { id }, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(userKeys.detail(id), context.previousUser);
      }
    },
    
    // Refetch on success
    onSuccess: (data, { id }) => {
      // Update detail cache with server response
      queryClient.setQueryData(userKeys.detail(id), data);
      
      // Invalidate lists to show updated user
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Hook to soft-delete user
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic removal from lists
 * - Rollback on error
 * - Invalidates all related queries
 * 
 * @example
 * const deleteUser = useDeleteUser();
 * 
 * await deleteUser.mutateAsync('user_123');
 */
export function useDeleteUser(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminUsersApi.softDeleteUser(id),
    
    // Optimistic update - remove from lists
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: userKeys.lists() });

      // Snapshot all list queries
      const previousLists = queryClient.getQueriesData<
        PaginatedResponse<AdminUser>
      >({
        queryKey: userKeys.lists(),
      });

      // Optimistically remove from all cached lists
      queryClient.setQueriesData<PaginatedResponse<AdminUser>>(
        { queryKey: userKeys.lists() },
        (old) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.filter((user) => user.id !== id),
            meta: {
              ...old.meta,
              total: old.meta.total - 1,
            },
          };
        },
      );

      return { previousLists };
    },
    
    // Rollback on error
    onError: (_error, _id, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    
    // Refetch on success
    onSuccess: (_data, id) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: userKeys.detail(id) });
      
      // Invalidate all lists to get fresh totals
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Hook to send welcome email to professional (T074 - US8)
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Sends welcome email to SERVICE_PROVIDER or FINANCIAL_ADVISOR
 * - Returns timestamp of when email was sent
 * - Error handling for non-professional users
 * 
 * @example
 * const sendWelcomeEmail = useSendWelcomeEmail();
 * 
 * await sendWelcomeEmail.mutateAsync('user_123');
 */
export function useSendWelcomeEmail(): UseMutationResult<
  SendWelcomeEmailResponse,
  Error,
  string
> {
  return useMutation({
    mutationFn: (id: string) => adminUsersApi.sendWelcomeEmail(id),
  });
}
